/**
 * User Management Service
 * API client for user, role, and location admin management
 */
import { apiClient } from './api.client';
import {
  UserManagement,
  CreateUserRequest,
  UpdateUserRequest,
  UpdateUserRoleRequest,
  UserListResponse,
  Role,
  RoleListResponse,
  Screen,
  ScreenListResponse,
  ScreenPermission,
  RoleScreenPermissionsResponse,
  UpdateRoleScreenPermissionsRequest,
  LocationAdmin,
  LocationAdminListResponse,
  LocationAdminAssignmentRequest,
  UserLocationAssignmentsResponse
} from '../types';

// ============================================
// USER MANAGEMENT
// ============================================

/**
 * Get paginated list of users
 */
export const getUsers = async (params?: {
  search?: string;
  role_id?: number;
  is_active?: boolean;
  include_child_companies?: boolean;
  page?: number;
  page_size?: number;
}): Promise<UserListResponse> => {
  const queryParams = new URLSearchParams();
  
  if (params?.search) queryParams.append('search', params.search);
  if (params?.role_id !== undefined) queryParams.append('role_id', params.role_id.toString());
  if (params?.is_active !== undefined) queryParams.append('is_active', params.is_active.toString());
  if (params?.include_child_companies !== undefined) queryParams.append('include_child_companies', params.include_child_companies.toString());
  if (params?.page) queryParams.append('page', params.page.toString());
  if (params?.page_size) queryParams.append('page_size', params.page_size.toString());
  
  const response = await apiClient.get<UserListResponse>(`/users?${queryParams.toString()}`);
  return response;
};

/**
 * Get user by profile ID
 */
export const getUserById = async (profileId: number): Promise<UserManagement> => {
  const response = await apiClient.get<UserManagement>(`/users/${profileId}`);
  return response;
};

/**
 * Create new user
 */
export const createUser = async (data: CreateUserRequest): Promise<{ success: boolean; message: string; data?: UserManagement }> => {
  const response = await apiClient.post<{ success: boolean; message: string; data?: UserManagement }>('/users', data);
  return response;
};

/**
 * Update user details
 */
export const updateUser = async (profileId: number, data: UpdateUserRequest): Promise<{ success: boolean; message: string }> => {
  const response = await apiClient.put<{ success: boolean; message: string }>(`/users/${profileId}`, data);
  return response;
};

/**
 * Update user role (admin only)
 */
export const updateUserRole = async (profileId: number, data: UpdateUserRoleRequest): Promise<{ success: boolean; message: string }> => {
  const response = await apiClient.put<{ success: boolean; message: string }>(`/users/${profileId}/role`, data);
  return response;
};

/**
 * Deactivate user
 */
export const deleteUser = async (profileId: number): Promise<{ success: boolean; message: string }> => {
  const response = await apiClient.delete<{ success: boolean; message: string }>(`/users/${profileId}`);
  return response;
};

// ============================================
// ROLE MANAGEMENT
// ============================================

/**
 * Get all roles
 */
export const getRoles = async (): Promise<RoleListResponse> => {
  const response = await apiClient.get<RoleListResponse>('/roles');
  return response;
};

/**
 * Get screen permissions for a role
 */
export const getRoleScreenPermissions = async (roleId: number): Promise<RoleScreenPermissionsResponse> => {
  const response = await apiClient.get<RoleScreenPermissionsResponse>(`/roles/${roleId}/screens`);
  return response;
};

/**
 * Update screen permissions for a role
 */
export const updateRoleScreenPermissions = async (
  roleId: number,
  data: UpdateRoleScreenPermissionsRequest
): Promise<{ success: boolean; message: string }> => {
  const response = await apiClient.put<{ success: boolean; message: string }>(`/roles/${roleId}/screens`, data);
  return response;
};

// ============================================
// SCREEN MANAGEMENT
// ============================================

/**
 * Get all screens
 */
export const getScreens = async (): Promise<ScreenListResponse> => {
  const response = await apiClient.get<ScreenListResponse>('/screens');
  return response;
};

/**
 * Get current user's screen permissions
 */
export const getMyScreenPermissions = async (locationId?: string): Promise<RoleScreenPermissionsResponse> => {
  const response = await apiClient.get<RoleScreenPermissionsResponse>('/screens/my-permissions',
    locationId && locationId !== 'all' ? { location_id: locationId } : undefined);
  return response;
};

// ============================================
// LOCATION ADMIN ASSIGNMENT
// ============================================

/**
 * Assign admin to location
 */
export const assignAdminToLocation = async (
  locationId: number,
  data: LocationAdminAssignmentRequest
): Promise<{ success: boolean; message: string }> => {
  const response = await apiClient.post<{ success: boolean; message: string }>(`/locations/${locationId}/admins`, data);
  return response;
};

/**
 * Get admins for a location
 */
export const getLocationAdmins = async (locationId: number): Promise<LocationAdminListResponse> => {
  const response = await apiClient.get<LocationAdminListResponse>(`/locations/${locationId}/admins`);
  return response;
};

/**
 * Remove admin from location
 */
export const removeAdminFromLocation = async (
  locationId: number,
  userProfileId: number
): Promise<{ success: boolean; message: string }> => {
  const response = await apiClient.delete<{ success: boolean; message: string }>(`/locations/${locationId}/admins/${userProfileId}`);
  return response;
};

/**
 * Get user's location assignments
 */
export const getUserLocationAssignments = async (userProfileId: number): Promise<UserLocationAssignmentsResponse> => {
  const response = await apiClient.get<UserLocationAssignmentsResponse>(`/v2/users/${userProfileId}/locations`);
  return response;
};

// ============================================
// UTILITY FUNCTIONS
// ============================================

/**
 * Check if user has permission based on role level
 */
export const hasPermission = (userRoleLevel: number | undefined, requiredLevel: number): boolean => {
  if (!userRoleLevel) return false;
  return userRoleLevel <= requiredLevel;
};

/**
 * Role level constants
 */
export const ROLE_LEVELS = {
  SUPER_ADMIN: 1,
  ADMIN: 10,
  LOCATION_ADMIN: 20,
  USER: 100,
  VIEWER: 200
} as const;

/**
 * Get role badge color
 */
export const getRoleBadgeColor = (roleLevel: number | undefined): string => {
  if (!roleLevel) return 'gray';
  
  if (roleLevel <= 1) return 'purple';  // Super Admin
  if (roleLevel <= 10) return 'blue';   // Admin
  if (roleLevel <= 20) return 'green';  // Location Admin
  if (roleLevel <= 100) return 'yellow'; // User
  return 'gray';  // Viewer
};

/**
 * Format user display name
 */
export const formatUserName = (user: UserManagement): string => {
  return `${user.first_name} ${user.last_name}`.trim();
};

/**
 * Check if user is active
 */
export const isUserActive = (user: UserManagement): boolean => {
  return user.status === 'Y' && user.is_active;
};

export default {
  // User management
  getUsers,
  getUserById,
  createUser,
  updateUser,
  updateUserRole,
  deleteUser,
  
  // Role management
  getRoles,
  getRoleScreenPermissions,
  updateRoleScreenPermissions,
  
  // Screen management
  getScreens,
  getMyScreenPermissions,
  
  // Location admin
  assignAdminToLocation,
  getLocationAdmins,
  removeAdminFromLocation,
  getUserLocationAssignments,
  
  // Utilities
  hasPermission,
  ROLE_LEVELS,
  getRoleBadgeColor,
  formatUserName,
  isUserActive
};
